import { useState, Suspense, useEffect } from "react";
import { RENTER_STATUS } from "../../../../../types/renter.type";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../../common/components/ui/loading/Loading";
import {
  TableFiltersState,
  TableFilters,
} from "../../../../../common/components/ui/table/TableFilters";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import { ICommission } from "../../../../../types/commission.type";
import { formatDateAndTime } from "../../../../../common/helpers/date.helpers";
import { getAllActivations } from "@/common/services/commission.api";

const STATUS_LABELS = {
  [RENTER_STATUS.Ativo]: "Ativo",
  [RENTER_STATUS.Incompleto]: "Incompleto",
  [RENTER_STATUS.PendentePagamento]: "Pendente de pagamento",
  [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para aprovação",
  [RENTER_STATUS.ReservaPaga]: "Reserva paga",
  [RENTER_STATUS.ReservaPendentePagamento]: "Reserva pendente de pagamento",
  [RENTER_STATUS.Finished]: "Finalizado",
  [RENTER_STATUS.Recision]: "Recisão",
  [RENTER_STATUS.Recall]: "Recolhimento",
  [RENTER_STATUS.WaitingRescision]: "Aguardando recisão",
  [RENTER_STATUS.Congelado]: "Congelado",
};

const TYPE_COMMISSION_LABELS: Record<string, string> = {
  activation: "Ativações",
  "activation-plus": "Ativações Plus",
  renew: "Renovações",
};

type CommissionProps = {
  commission: ICommission[];
};

function Activation(props: CommissionProps) {
  const commission = props.commission;
  const headers = {
    sdr: "SDR",
    renterName: "Nome",
    licensePlate: "Placa",
    activationDate: "Ativado em",
    typeCommission: "Comissão",
    depositPayment: "Pagamento do caução",
    weeklyPayment: "Pagamento da semanal",
  };
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [commissionActivation, setCommission] = useState<ICommission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-activationDate",
  });
  const [sdrTotal, setSdrTotal] = useState(0);
  const [consultantTotal, setConsultantTotal] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });

  const technicalConsultant = commission[0].technicalConsultant;
  useEffect(() => {
    async function loadComissionActivation(filters, page) {
      setIsLoading(true);
      try {
        const result = await getAllActivations({
          page,
          ...filters,
          technicalConsultant,
        });
        if (!result.ok) {
          notifyError("Erro ao carregar ativações.");
          return;
        }

        setCommission(result.data);
        setData({ itemsPerPage, totalItems: result.totalItems });
        setSdrTotal(result.sdrTotal);
        setConsultantTotal(result.consultantTotal);
      } catch (err) {
        notifyError("Erro inesperado ao carregar ativações.");
      } finally {
        setIsLoading(false);
      }
    }

    if (technicalConsultant !== "") {
      loadComissionActivation(filters, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicalConsultant, page, filters.search, filters]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="tabs-border-line-commission">
        <div className="tabs-item-style-commission">
          <TableFilters
            dashboardHeader="Ativações"
            placeholder="nome"
            statusOptions={STATUS_LABELS}
            newItemLabel="Novo Cliente"
            onSearch={handleFilterChange}
            entity="commission"
            tableData={commissionActivation}
          />

          <h2 className="font-semibold">{`Resumo das Ativações - ${technicalConsultant}`}</h2>
          <div className="flex gap-4 mb-4">
            <div className="bg-white shadow p-4 rounded-xl w-full">
              <p className="text-sm font-medium text-gray-600">Comissão SDR</p>
              <p className="text-lg font-bold text-green-600">
                R$ {sdrTotal.toFixed(2)}
              </p>
            </div>
            <div className="bg-white shadow p-4 rounded-xl w-full">
              <p className="text-sm font-medium text-gray-600">
                Comissão Consultor
              </p>
              <p className="text-lg font-bold text-green-600">
                R$ {consultantTotal.toFixed(2)}
              </p>
            </div>
          </div>

          <GenericTable
            headers={headers}
            data={commissionActivation}
            setFilters={({ orderBy }) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                orderBy,
              }));
            }}
            renders={
              {
                renterName: (name, commission) => (
                  <a
                    href={`/clientes/${commission.renterId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/clientes/${commission.renterId}`);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {name as string}
                  </a>
                ),
                licensePlate: (licensePlate, comission) => (
                  <a
                    href={`/veiculos/${
                      (comission as ICommission).licensePlate
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        `/veiculos/${(comission as ICommission).licensePlate}`
                      );
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {licensePlate as string}
                  </a>
                ),
                activationDate: formatDateAndTime,
                currentContractStartDate: formatDateAndTime,
                typeCommission: (type: string) =>
                  TYPE_COMMISSION_LABELS[type] || type,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            entity="commissions"
          />

          {isLoading && (
            <div className="fixed top-0 left-0 right-0 bottom-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Loading />
            </div>
          )}

          <TableFooter
            page={page || 1}
            totalItems={data.totalItems || 0}
            itemsPerPage={+(filters.itemsPerPage ?? 10)}
            goToPage={setPage}
          />
        </div>
      </div>
    </Suspense>
  );
}

export default Activation;
